import { ToastAlertProvider } from '@design-systems/canvas-ui'
import { HelveticaForTarget } from '@enterprise-ui/component-font'
import { OpenTelemetryManager } from '@measurement/opentelemetry-web'
import { AnalyticsProvider } from '@praxis/analytics/client'
import { AuthProvider } from '@praxis/component-auth'
import { EnvProvider } from '@praxis/component-runtime-env'
import { getDomainName } from 'mvscommoncomponents'
import { useEffect, useRef } from 'react'
import { BrowserRouter as Router } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'
import './globalStyles/customStyles.scss'
import { Main } from './views/Main'

export const config = {}
export const appContants = {}

export const isExternalDomain =
  getDomainName()?.toLowerCase() === 'partnersonline.com'
const LOGIN_REDIRECT = window.location.origin

const OpenTelemetryInitializer = ({ env }) => {
  const initializedRef = useRef(false)

  useEffect(() => {
    if (initializedRef.current) return
    if (!env?.openTelemetry?.enable) return

    const extractUrls = (obj) =>
      Object.values(obj || {}).flatMap((val) =>
        typeof val === 'string'
          ? ['/' + val.split('?')[0]]
          : typeof val === 'object'
            ? extractUrls(val)
            : [],
      )
    const urlTemplates = extractUrls(env?.mfelcm?.services)
    OpenTelemetryManager.initialize({
      apiKey: env.openTelemetry.apiKey,
      serviceName: env.openTelemetry.serviceName,
      clusterName: env.openTelemetry.clusterName,
      environment: env.openTelemetry.env,
      urlTemplates,
      globalLogLevel: 'WARN',
    })
    initializedRef.current = true
  }, [env])

  return null
}

const App = (props) => {
  //Override body overflow to hidden to prevent page scrolls
  document.querySelector('html').style.overflow = 'hidden'
  document.querySelector('body').style.overflow = 'hidden'

  useEffect(function onMount() {
    const rootEle = document.getElementById('root')
    if (rootEle) {
      document.getElementById('root').id = 'LCM'
    }
  }, [])

  useEffect(function onUnMount() {
    return () => {
      //Restore overflow
      document.querySelector('html').style.overflow = null
      document.querySelector('body').style.overflow = null
      const rootElement = document.getElementById('LCM')
      if (rootElement) {
        rootElement.id = 'root'
      }
    }
  }, [])

  return (
    <EnvProvider
      configPath={
        process.env.NODE_ENV === 'development'
          ? '/config.json'
          : '/app-environment'
      }
    >
      {({ env }) => {
        return (
          <>
            <OpenTelemetryInitializer env={env} />
            <HelveticaForTarget variants={['n4', 'n5', 'n7']} />
            <AuthProvider
              clientId={env.clientId}
              loginRedirect={LOGIN_REDIRECT}
              authorizationUrl={
                isExternalDomain ? env.externalAuthUrl : env.internalAuthUrl
              }
              logoutUrl={
                isExternalDomain ? env.externalLogoutUrl : env.internalLogoutUrl
              }
            >
              <ToastContainer />
              <Router basename="/lcm">
                <AnalyticsProvider
                  config={{
                    apiKey: env?.firefly?.apiKey || '',
                    appName: env?.firefly?.appName || '',
                    url: env?.firefly.url,
                  }}
                  trackLocations
                >
                  <ToastAlertProvider location="top">
                    <Main {...props} isExternalDomain={isExternalDomain} />
                  </ToastAlertProvider>
                </AnalyticsProvider>
              </Router>
            </AuthProvider>
          </>
        )
      }}
    </EnvProvider>
  )
}

export default App
